import { fabric } from 'fabric';
import CanvasObject from "./CanvasObject";
import Canvas from "./../Canvas";
import apiRequestHelper from "./../../api/requestHelper";
import AppDebug from "./../../AppDebug";

class ImageObject extends CanvasObject {

    static TYPE_TITLE = 'image';

    /**
     * Image when locally added. (base64 encoded)
     * @type ?string
     */
    _imageContent;

    /**
     * Image when loaded from the Rest-API. (url)
     * @type ?string
     */
    _imageUrl;

    /**
     * Stores a image element.
     * @type Image
     */
    _image;
    _imageIsManipulable;

    /*
     * Ratio of the image (height / width)
     * @type number
     */
    _imageRatio;

    /**
     * Filename of image in local filesystem.
     * @type string
     */
    _filename;

    /**
     * The image object can be automatically adapted to the size of the BoundingBox during initialization, but with an existing object
     * from the database this behaviour is undesirable and must therefore be explicitly activated if permitted in this situation.
     * @type boolean
     */
    _isResizingAllowed;

    _label;
    _labelSecondLanguage;

    /**
     * If false, the object will not be saved. Allows the object to be saved only under conditions, for example after selecting a new image.
     * @type boolean
     */
    _print;

    /**
     * Create a ImageObject.
     * @param {Canvas} canvas
     * @param {?string} imageContent A Base64 encoded image if it has not been uploaded yet, only added locally. If already uploaded null.
     * @param {?string} imageUrl URL if the image has already been uploaded, otherwise null by default.
     * @param {string} filename
     * @param {boolean} isResizingAllowed
     */
    constructor(canvas, imageContent, imageUrl = null, filename = '', isResizingAllowed = false) {
        super(canvas);

        this._filename = filename;
        this._image = null;

        if (imageContent === null) {
            this._imageContent = null;
            this._imageUrl = imageUrl;
        } else {
            this._imageContent = imageContent;
            this._imageUrl = null;
        }

        this._isResizingAllowed = isResizingAllowed;

        this.setImageIsManipulable(true);

        // Set default values.
        this.initDefaultValues();

        this._print = true;

    }

    /**
     * Returns a new FabricObject that can be added to the canvas.
     * @returns {Promise<fabric.Object>}
     */
    async createFabricObject() {

        if (this.getVisibility() === 2 && window['w2p_admin'] !== true)
            return null;

        // The first time a fabric.object is created, the cached image object must be created.
        // The cache is initialized with zero.
        if (! this._image)
            await this.createImageStore();

        // Use cached image element.
        const imageFabricObject = new fabric.Image( this._image );

        imageFabricObject.set({
            selectable: this.getIsSelectable() || window['w2p_admin'] === true,
            evented: this.getIsSelectable() || window['w2p_admin'] === true,
            id: this.getId(),
            left: this.getLeft(),
            top: this.getTop(),
            height: this.getHeight(),
            width: this.getWidth(),
            scaleX: this.getScaleX(),
            scaleY: this.getScaleY(),
            angle: this.getAngle(),
            borderColor: this.getExceedsBoundingBox() ? 'red' : 'rgba(102,153,255,0.75)'
        });

        if (this.getIsLocked()) {
            imageFabricObject.set({selectable: false, evented: false});
        }

        return imageFabricObject;

    }

    async removeImage() {
        this.setImageUrl('img/defaulttranparency.png');
        await this.createImageStore();
        this._print = false;
        this.getCanvas().render();
    }

    /**
     * Object that is passed in JSON format to the API when saving the CanvasObject.
     * @returns {Object}
     */
    createObjectForSaveRequest(configurationViewId, imageId) {

        return {
            serializedObject: {
                isSelectable: this.getIsSelectable(),
                deletable: this.getDeletable(),
                fill: null,
                label: {
                    value: this.getLabel()
                },
                labelSecondLanguage: {
                    value: this.getLabelSecondLanguage()
                },
                left: {
                    value: this.getLeft(),
                    manipulable: this.getLeftIsManipulable(),
                    whitelist: this.getLeftWhitelist()
                },
                canvasRelativeLeft: {
                    value: this.getLeftBoundingBox(),
                    manipulable: this.getLeftBoundingBoxIsManipulable(),
                    whitelist: this.getLeftBoundingBoxWhitelist()
                },
                top: {
                    value: this.getTop(),
                    manipulable: this.getTopIsManipulable(),
                    whitelist: this.getTopWhitelist()
                },
                canvasRelativeTop: {
                    value: this.getTopBoundingBox(),
                    manipulable: this.getTopBoundingBoxIsManipulable(),
                    whitelist: this.getTopBoundingBoxWhitelist()
                },
                width: {
                    value: this.getWidth(),
                    manipulable: this.getWidthIsManipulable(),
                    whitelist: this.getWidthWhitelist()
                },
                height: {
                    value: this.getHeight(),
                    manipulable: this.getHeightIsManipulable(),
                    whitelist: this.getHeightWhitelist()
                },
                angle: {
                    value: this.getAngle(),
                    manipulable: this.getAngleIsManipulable(),
                    whitelist: this.getAngleWhitelist()
                },
                type: this.constructor.TYPE_TITLE,
                positionLimits: {
                    min: {
                        left: this._minBoundingBoxLeft,
                        top: this._minBoundingBoxTop,
                        right: this._minBoundingBoxRight,
                        bottom: this._minBoundingBoxBottom
                    },
                    max: {
                        left: this._maxBoundingBoxLeft,
                        top: this._maxBoundingBoxTop,
                        right: this._maxBoundingBoxRight,
                        bottom: this._maxBoundingBoxBottom
                    }
                },
                fontSize: null,
                fontFamily: null,
                text: null,
                image: {
                    value: this.getFilename(),
                    manipulable: this.getImageIsManipulable()
                },
                scaleX: this.getScaleX(),
                scaleY: this.getScaleY(),
                visibility: this.getVisibility(),
                zPosition: this.getZPosition(),
                exceedsBoundingBox: this.getExceedsBoundingBox(),
                print: this._print
            },
            productConfigurationViewId: configurationViewId,
            imageId: imageId || this.getImageUrl() // TODO imageUrl to right format
        };

    }

    /**
     * A post request is sent to the API and attempts to create the object.
     * @param {number} configurationViewId
     * @return {Promise<void>}
     */
    async save(configurationViewId) {

        if (! this._print && window['w2p_admin'] !== true) {
            return;
        }

        this.readFromFabricObject();

        let imageId = null;

        if (this.getImageUrl() === null) {
            const image = await apiRequestHelper.requestApiRoute(
                'post',
                '/image',
                {
                    image: this.getImageContent(),
                    filename: this.getFilename()
                });
            imageId = image.id;
        } else {

            imageId = this.getImageUrl().trim().split('/');
            imageId = imageId[imageId.length - 1];
            imageId = parseInt(imageId);

            if (isNaN(imageId)) {
                throw `Image ID of the object with ID ${this.getId()} cannot be determined. Saving this image object is canceled.`;
                return;
            }

        }

        await apiRequestHelper.requestApiRoute(
            'post',
            '/object',
            this.createObjectForSaveRequest(configurationViewId, imageId),
            error => AppDebug.api.events.createCanvasObjectFailed({error})
        );

    }

    /**
     * Creates the picture element from a URL or a Base64 encoded picture and stores it in the picture cache.
     * It also adjusts the width and height of the object.
     * @returns {Promise<void>}
     * @private
     */
    async createImageStore(init = false) {

        if (this._print === false && !init) {
            this._print = true;
        }

        if ( this._imageContent ) {

            // if locally image
            this._image = await this.createImageElement(this._imageContent);

        } else {

            // if the image has already been uploaded
            this._image = await this.createImageElement(this._imageUrl);

        }

        this.setHeight( this._image.height );
        this.setWidth( this._image.width );
        this.setImageRatio(this._image.height / this._image.width);

        if ( this._isResizingAllowed === true && ( this.getCanvas().getBoundingBox().getWidth() < this.getWidth() || this.getCanvas().getBoundingBox().getHeight() < this.getHeight() ) ) {

            let factor = Math.min(this.getCanvas().getBoundingBox().getScaledWidth() / this.getWidth(), this.getCanvas().getBoundingBox().getScaledHeight() / this.getHeight());

            this.setScaleX( factor );
            this.setScaleY( factor );

        }

        if (this._maxBoundingBoxRight) {
            const limit = this.getCanvas().convertMmInPixel(this._maxBoundingBoxRight) - this.getLeft();
            if (this.getScaledWidth() > limit) {
                const factor = limit / this.getWidth(); // 296.4 / 457.216 - 0.1
                this.setScaleX(factor);
                this.setScaleY(factor);
            }
        }

        if (this._minBoundingBoxLeft) {
            const allowedSpaceWidth = this.getCanvas().getWidth() - this.getCanvas().convertMmInPixel(this._minBoundingBoxLeft);
            const limit = allowedSpaceWidth - (this.getLeft() - this.getCanvas().convertMmInPixel(this._minBoundingBoxLeft));
            console.log(this.getScaledWidth(), {limit});
            if (this.getScaledWidth() > limit) {
                console.log('rescale', this.getScaledWidth(), {limit});
                const factor = limit / this.getWidth();
                this.setScaleX(factor);
                this.setScaleY(factor);
            }
        }

        this._isResizingAllowed = false;

    }

    /**
     * Creates async an image element that can be passed to fabric.Image constructor.
     * @returns {Promise<Image>}
     * @private
     */
    createImageElement(source) {

        return new Promise((resolve, reject) => {

            const imageElement = new Image();

            imageElement.addEventListener('load',
                () => resolve(imageElement)
            );
            imageElement.addEventListener('error', reject);

            imageElement.src = source;

        });

    }

    /**
     * Each type of object can define how its displayed in list of objects from current active product configuration view.
     * @returns {string} Filename
     */
    getCardTitleInObjectsConfigurator() {

        return {
            image: 'img/icon_type_image.png',
            title: this.getFilename()
        };

    }

    /**
     * Each type of object can define its own view in which controls are defined for the object.
     * @returns {string} Filename
     */
    getViewInObjectsConfigurator() {

        return 'ImageCanvasObject.vue';

    }

    allowResizing() {
        this._isResizingAllowed = true;
    }

    getFilename() {
        return this._filename;
    }

    setFilename(value) {
        this._filename = value;
    }

    getLabel() {
        return this._label;
    }

    setLabel(value) {
        this._label = value;
    }

    getLabelSecondLanguage() {
        return this._labelSecondLanguage;
    }

    setLabelSecondLanguage(value) {
        this._labelSecondLanguage = value;
    }

    getImageContent() {
        return this._imageContent;
    }

    setImageContent(value) {
        if ( this.isManipulableAndWhitelistContainsValue('image', value) === false )
            return;

        this._image = null;
        this._imageUrl = null;
        this._imageContent = value;
    }

    getImageUrl() {
        return this._imageUrl;
    }

    setImageUrl(value) {
        if ( this.isManipulableAndWhitelistContainsValue('image', value) === false )
            return;

        this._image = null;
        this._imageContent = null;
        this._imageUrl = value;
    }

    getImageRatio() {
        return this._imageRatio;
    }

    setImageRatio(value) {
        this._imageRatio = value;
    }

    getImageIsManipulable() {
        return this._imageIsManipulable;
    }

    setImageIsManipulable(value) {
        this._imageIsManipulable = value;
    }

    isVisibleInSidebar() {

        if (
            super.isVisibleInSidebar() ||
            this.getImageIsManipulable()

        ) {
            return true;
        }

        return false;

    }

}

export default ImageObject;
